import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listEquipment, deactivateEquipment, listMolds, deactivateMold, type EquipmentListItem, type MoldListItem } from '../../api/echipamente'
import { errorMessage } from '../../api/client'
import EquipmentFormDialog from './EquipmentFormDialog'
import MoldFormDialog from './MoldFormDialog'

const STATUS_LABELS: Record<string, string> = {
  Functional: 'Funcțional', InMentenanta: 'În mentenanță', Defect: 'Defect', Casat: 'Casat',
}
const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  Functional: 'success', InMentenanta: 'warning', Defect: 'error', Casat: 'default',
}

export default function EquipmentPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Mentenanță</Typography>
        <Typography variant="inherit" color="text.primary">Echipamente și matrițe</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Echipamente și matrițe</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Utilaje" />
        <Tab label="Matrițe" />
      </Tabs>
      {tab === 0 ? <EquipmentTab /> : <MoldsTab />}
    </Stack>
  )
}

function EquipmentTab() {
  const [formTarget, setFormTarget] = useState<EquipmentListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: EquipmentListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateEquipment,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['mentenanta', 'echipamente'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<EquipmentListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'manufacturer', headerName: 'Producător', width: 140, renderCell: (p) => p.row.manufacturer ?? '—' },
    { field: 'model', headerName: 'Model', width: 120, renderCell: (p) => p.row.model ?? '—' },
    { field: 'responsibleUserName', headerName: 'Responsabil', width: 160, renderCell: (p) => p.row.responsibleUserName ?? '—' },
    {
      field: 'status', headerName: 'Stare', width: 130,
      renderCell: (p) => <Chip label={STATUS_LABELS[p.row.status] ?? p.row.status} size="small" color={STATUS_COLORS[p.row.status] ?? 'default'} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă utilaj</Button>
      </Box>
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <EquipmentFormDialog equipment={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function MoldsTab() {
  const [formTarget, setFormTarget] = useState<MoldListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: MoldListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['mentenanta', 'matrite'], queryFn: listMolds })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateMold,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['mentenanta', 'matrite'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<MoldListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'compatibleEquipmentCount', headerName: 'Utilaje compatibile', width: 160, type: 'number' },
    { field: 'compatibleArticleCount', headerName: 'Articole formate', width: 150, type: 'number' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă matriță</Button>
      </Box>
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <MoldFormDialog mold={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
